<template>
    <div class="transactions-list__wrapper">
      <div v-if="loading" style="padding: 24px 0">
        <BaseLoader />
      </div>
      <template v-else-if="transactionList.length">
        <div class="transactions-header">
          <div class="title">{{ title }}</div>
          <DateRangePicker v-model="dates" />
        </div>
        <div class="transaction" v-for="(item, idx) in transactionListPaginated" :key="idx">
          <TransactionTypesIcon :type="type(item)" />
          <div class="text">
            <div class="left-side">
              <div class="name">{{ text(`transactions.list.${type(item)}`) }}</div>
              <div class="status" :class="item.status">{{ text(`transactions.list.status.${item.status}`) }}</div>
            </div>
            <div class="right-side">
              <div class="amount" :class="type(item) === 'withdrawal' ? 'minus' : 'plus'"> {{ parseFloat(item.amount) }} {{getCurrencySymbol(item.currency_code)}}</div>
              <div class="time">{{ formatDateTime(item.created_at) }}</div>
            </div>
          </div>
        </div>
        <div class="transactions-pagination-block">
          <Pagination v-model="currentPage" :total="transactionListFiltered.length" :items-per-page="itemsPerPage" />
        </div>
      </template>
      <div v-else class="empty" >
          <TransactionsEmptyState />
      </div>
    </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';

export default {
    name: "Transactions",
    components: {
      BaseLoader: defineAsyncComponent (() => import(/* webpackChunkName: "components/base/BaseLoader.vue" */ '@/components/base/BaseLoader.vue')),
      TransactionsEmptyState: defineAsyncComponent (() => import(/* webpackChunkName: "components/transactions/TransactionsEmptyState" */ '@/components/transactions/TransactionsEmptyState.vue')),
      TransactionTypesIcon: defineAsyncComponent (() => import(/* webpackChunkName: "components/transactions/icons/Types" */ './icons/Types.vue')),
      DateRangePicker: defineAsyncComponent (() => import(/* webpackChunkName: "components/DateRangePicker" */ '@/components/DateRangePicker.vue')),
      Pagination: defineAsyncComponent (() => import(/* webpackChunkName: "components/base/pagination" */ '@/components/base/pagination/index.vue')),
    },
    props: {
        title: {
            type: String,
            default: ''
        }
    },
    data () {
        return {
            dates: [],
            transactionList: [],
            currentPage: 1,
            itemsPerPage: 10,
            loading: false
        }
    },
    created() {
        this.getTransactionList()
    },
    computed: {
      authToken() {
          return this.$store.state.authToken
      },
      transactionListFiltered({ dates }) {
        return this.transactionList.filter((item) => {
          return dates[0] < new Date(item.created_at) && dates[1] > new Date(item.created_at)
        })
      },
      transactionListPaginated({ transactionListFiltered , currentPage, itemsPerPage }) {
        const start = (currentPage - 1) * itemsPerPage
        return transactionListFiltered.slice(start, start + itemsPerPage)
      }
    },
    methods: {
        type(transaction) {
          if (Number(transaction.amount) === 0) {
            return 'operation'
          } else if (Number(transaction.amount) > 0) {
            return 'topup'
          } else if (Number(transaction.amount) < 0) {
            return 'withdrawal'
          }
        },
        async getTransactionList() {
            this.loading = true
            const { data } = await this.$http.get(
                '/v2/wallet/transaction?ordering=-created_at',
                {
                    "headers": {
                        "Authorization": "Token " + this.authToken
                    }
                }
            )

            this.loading = false
            this.transactionList = data.results;
        }
    }
}
</script>

<style lang="scss" scoped>
  .transactions-list__wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;

    border-radius: 16px;
    padding: 16px;
    background: var(--element-bg);

    .empty {
      height: calc(100dvh - 934px);
      min-height: 200px;
    }

    .transactions-header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .title {
        padding: 0 0 10px 0;
        color: #FFF;
        font-family: "Rubik", sans-serif;
        font-size: 18px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
        letter-spacing: 0.36px;
      }
    }
  }

  .transaction {
    display: flex;
    align-items: center;
    padding: 8px 0;
    gap: 12px;

    &:not(:last-child) {
      border-width: 0 0 1px 0;
      border-style: solid;
      border-color: var(--layout-bg-lighter);
    }
  }
  .transaction img {
    width: 38px;
    height: 38px;
    -o-object-fit: cover;
    object-fit: cover;
  }
  .transaction .text {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .left-side {
      display: flex;
      flex-direction: column;
      gap: 5px;

      .name {
        font-size: 16px;
      }
      .status {
        font-size: 14px;
        font-weight: 500;

        &.failed {
          color: #F34B47;
        }
        &.complete {
          color: #00BF8E;
        }
        &.pending {
          color: #CCCC00;
        }
      }
    }
    .right-side {
      display: flex;
      flex-direction: column;
      gap: 5px;
      align-items: end;

      .amount {
        font-size: 16px;

        &.plus {
          color: #00BF8E;
          &::before {
            content: "+";
          }
        }
        &.minus {
          color: #F34B47;
          &::before {
            content: "";
          }
        }
      }
      .time {
        color: var(--text-color-secondary);
        font-size: 12px;
      }
    }
  }

  .transactions-pagination-block {
    padding: 15px 0 5px;
  }

  @media (max-width: 920px) {
    .transactions-list__wrapper {
      background: var(--layout-bg);
    }
    .transactions-list__wrapper .transaction .text .text_bottom .time {
      font-size: 12px;
    }
  }
</style>